import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Mail, Instagram } from 'lucide-react';
import { projects, pickLang } from '../mock';
import aboutImage from '../assets/about.jpg';
import HeroOverlay from '../components/HeroOverlay';
import FrostVeil from '../components/FrostVeil';
import { useTranslation } from 'react-i18next';

// Vidéos servies depuis /public/videos (pas bundlées par webpack) pour un chargement plus rapide.
const heroVideo = '/videos/nikon.mp4';

export const Home = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'fr';
  const location = useLocation();
  const featuredProjects = projects.slice(0, 3);

  // refs pour scroll
  const aboutRef = useRef(null);
  const contactRef = useRef(null);

  // Scroll natif vers une section
  const scrollToSection = (ref) => {
    ref?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Intersection Observer pour fade-in
  useEffect(() => {
    const observerOptions = { threshold: 0.1, rootMargin: '0px 0px -50px 0px' };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fadeIn');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-element, .scroll-animate');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Scroll automatique depuis Header via query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const scrollTo = params.get('scrollTo');

    if (scrollTo === 'about' && aboutRef.current) {
      aboutRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    if (scrollTo === 'contact' && contactRef.current) {
      contactRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.search]);

  const categoryLabels = {
    Ads: t('portfolio.filter.ads'),
    Fiction: t('portfolio.filter.fiction'),
    Animation: t('portfolio.filter.animation'),
  };

  return (
    <div className="min-h-screen">

      {/* Hero Section — le showreel (vidéo) reste le sujet principal de l'écran,
          le titre est traité comme une légende de film en bas à gauche, pas
          une affiche qui écrase tout. */}
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Overlay et effets */}
        <HeroOverlay />
        <FrostVeil />

        {/* Repères d'angle façon viseur caméra */}
        <div className="absolute top-6 left-6 w-8 h-8 border-t border-l border-paper/30 z-10"></div>
        <div className="absolute top-6 right-6 w-8 h-8 border-t border-r border-paper/30 z-10"></div>
        <div className="absolute bottom-6 left-6 w-8 h-8 border-b border-l border-paper/30 z-10"></div>
        <div className="absolute bottom-6 right-6 w-8 h-8 border-b border-r border-paper/30 z-10"></div>

        {/* Content */}
        <div className="container mx-auto px-6 relative z-10 pb-20 pt-32">
          <div className="max-w-2xl fade-element relative">
            <div className="eyebrow hero-badge mb-4 relative">{t('home.hero.badge')}</div>

            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.94] uppercase mb-6 hero-title relative">
              <span className="grain-text block" style={{ backgroundColor: 'var(--color-paper)' }}>
                {t('home.hero.title').split(' ')[0]}
              </span>
              <span className="grain-text block" style={{ backgroundColor: 'var(--color-ember)' }}>
                {t('home.hero.title').split(' ').slice(1).join(' ')}
              </span>
            </h1>
            <p className="font-body text-base md:text-lg text-gray-300 mb-10 max-w-md leading-relaxed hero-subtitle relative">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4 hero-buttons fade-element relative">
              <Link to="/portfolio" className="btn-primary group">
                {t('home.hero.cta_portfolio')}
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={16} />
              </Link>
              <button onClick={() => scrollToSection(contactRef)} className="btn-ghost">
                {t('home.hero.cta_contact')}
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator — meme repere visuel (fleche ember qui rebondit)
            que sur les pages projet, centre en bas du hero plutot que collee
            a droite : plus visible, et plus coherent avec le reste du site. */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:block pointer-events-none">
          <svg className="scroll-arrow" width="52" height="52" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 4v16M12 20l-7-7M12 20l7-7"
              stroke="var(--color-ember)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 bg-ink-900 bg-textured defer-offscreen relative border-t border-white/5">
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12 fade-element">
            <h2 className="font-display text-3xl md:text-5xl uppercase">
              {t('home.featured_projects.title')} <em className="text-ember not-italic">{t('home.featured_projects.subtitle')}</em>
            </h2>
            <div className="meta-label">{String(featuredProjects.length).padStart(2, '0')} / {String(projects.length).padStart(2, '0')} — {t('home.featured_projects.selection_label').toUpperCase()}</div>
          </div>
          {/* Vraie grille avec espacement (gap) et bordure complète sur
              chaque carte plutôt qu'un fond de grille partagé dans les
              interstices : une rangée incomplète ne laisse ainsi jamais de
              bandeau vide coloré à la place d'une carte manquante. */}
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {featuredProjects.map((project, index) => (
              <Link
                key={project.id}
                to={`/project/${project.id}`}
                className="project-card group fade-element border border-white/10"
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                <img src={project.thumbnail} alt={pickLang(project.title, lang)} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-transparent"></div>
                <div className="holo-sheen"></div>
                <div className="project-panel">
                  <div className="grain"></div>
                  <div className="meta text-amber font-mono text-[11px] tracking-widest uppercase mb-1">
                    {categoryLabels[project.meta?.category] || project.meta?.category} — {project.meta?.year}
                  </div>
                  <h3 className="font-display text-2xl uppercase">{pickLang(project.title, lang)}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" ref={aboutRef} className="py-20 bg-ink-900 bg-textured defer-offscreen relative border-t border-white/5">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-stretch">
            <div className="fade-element" style={{ transitionDelay: '0.1s' }}>
              <div className="eyebrow mb-6">{t('home.about.eyebrow')}</div>
              <div className="font-body space-y-4 text-gray-300 leading-relaxed">
                <p>{t('home.about.paragraph1')}</p>
                <p>{t('home.about.paragraph2')}</p>
                <p>{t('home.about.paragraph3')}</p>
              </div>
            </div>
            {/* Carré à taille fixe (350x350), centré dans la colonne. */}
            <div className="relative opacity-0 translate-y-4 transition-all duration-700 ease-out"
                 ref={(el) => {
                   if (!el) return;
                   const observer = new IntersectionObserver(entries => {
                     entries.forEach(entry => {
                       if (entry.isIntersecting) {
                         entry.target.classList.remove('opacity-0', 'translate-y-4');
                         entry.target.classList.add('opacity-100', 'translate-y-0');
                         observer.unobserve(entry.target);
                       }
                     });
                   }, { threshold: 0.2 });
                   observer.observe(el);
                 }}>
              <div className="w-[350px] h-[350px] max-w-full mx-auto overflow-hidden border border-white/10 group">
                <img
                  src={aboutImage}
                  alt={t('home.about.title')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" ref={contactRef} className="py-20 bg-ink-950 bg-textured defer-offscreen relative border-t border-white/5">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="eyebrow mb-4 justify-center">{t('home.contact.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl uppercase mb-6 scroll-animate">
              {t('home.contact.title')}
            </h2>
            <p className="font-body text-gray-300 text-lg mb-12 scroll-animate" style={{ animationDelay: '0.1s' }}>
              {t('home.contact.subtitle')}
            </p>

            <div className="grid md:grid-cols-2 border border-white/10 divide-y md:divide-y-0 md:divide-x divide-white/10 mb-12">
              <a
                href={`mailto:${t('home.contact.email_address')}`}
                className="group p-8 hover:bg-ink-900 transition-colors duration-300 scroll-animate"
                style={{ animationDelay: '0.2s' }}
              >
                <Mail className="text-ember mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" size={28} />
                <h3 className="font-display text-xl uppercase mb-2 group-hover:text-ember transition-colors duration-300">{t('home.contact.email')}</h3>
                <p className="font-body text-gray-400 text-sm break-all group-hover:text-gray-300 transition-colors duration-300">{t('home.contact.email_address')}</p>
              </a>
              <a
                href="https://instagram.com/a_m.imagery"
                target="_blank"
                rel="noopener noreferrer"
                className="group p-8 hover:bg-ink-900 transition-colors duration-300 scroll-animate"
                style={{ animationDelay: '0.3s' }}
              >
                <Instagram className="text-ember mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" size={28} />
                <h3 className="font-display text-xl uppercase mb-2 group-hover:text-ember transition-colors duration-300">{t('home.contact.instagram')}</h3>
                <p className="font-body text-gray-400 text-sm group-hover:text-gray-300 transition-colors duration-300">{t('home.contact.instagram_handle')}</p>
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

